import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NachaService } from '../nacha.service';

@Component({
    selector: 'app-nacha-steps-dialog',
    templateUrl: './nacha-steps-dialog.component.html',
    styleUrls: ['./nacha-steps-dialog.component.css'],
    standalone: false
})
export class NachaStepsDialogComponent implements OnInit {

    generating = false;
    fileGenerated = false;
    markingSubmitted = false;
    submitted = false;
    generatedBatchId: number | null = null;
    errorMsg: string | null = null;

    alreadyBatched: any[] = [];
    eligible: any[] = [];

    steps: string[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            ownerStmtIds: number[];
            taxType: 'GET' | 'TAT' | 'MCTAT';
            previewRows: any[];
        },
        private dialogRef: MatDialogRef<NachaStepsDialogComponent>,
        private nachaService: NachaService
    ) {}

    ngOnInit(): void {
        this.buildSteps();
        // Separate already-batched from eligible
        const batchField = this.batchField();
        this.alreadyBatched = this.data.previewRows.filter(r => r[batchField] != null);
        this.eligible       = this.data.previewRows.filter(r => r[batchField] == null && this.getAmount(r) > 0);
    }

    private batchField(): string {
        switch (this.data.taxType) {
            case 'GET':   return 'getNachaBatchId';
            case 'TAT':   return 'tatNachaBatchId';
            case 'MCTAT': return 'mctatNachaBatchId';
        }
    }

    getAmount(row: any): number {
        switch (this.data.taxType) {
            case 'GET':   return row.getAmount   ?? 0;
            case 'TAT':   return row.tatAmount   ?? 0;
            case 'MCTAT': return row.mctatAmount ?? 0;
        }
    }

    get totalAmount(): number {
        return this.eligible.reduce((sum, r) => sum + this.getAmount(r), 0);
    }

    private buildSteps(): void {
        switch (this.data.taxType) {
            case 'GET':
                this.steps = [
                    'Log in to Hawaii Tax Online at hitax.hawaii.gov (username: Kmacapitalcorp)',
                    'Navigate to: File Return → General Excise/Use Tax → ACH Credit Payment',
                    'Click "Generate File" below to download the NACHA .ach file',
                    'Upload the downloaded file on the state website',
                    'Note the confirmation number, then click "Mark as Submitted" below'
                ];
                break;
            case 'TAT':
                this.steps = [
                    'Log in to Hawaii Tax Online at hitax.hawaii.gov (username: Kmacapitalcorp)',
                    'Navigate to: File Return → Transient Accommodations Tax → ACH Credit Payment',
                    'Click "Generate File" below to download the NACHA .ach file',
                    'Upload the downloaded file on the state website',
                    'Note the confirmation number, then click "Mark as Submitted" below'
                ];
                break;
            case 'MCTAT':
                this.steps = [
                    'Log in to the Maui County portal at mauicounty.ehawaii.gov/mvst/ (username: accounting@kbmresorts.com)',
                    'Enter each property\'s MCTAT amount manually in the web form',
                    'Click "Generate File" below to download a record of amounts for your files',
                    'After submitting on the portal, click "Mark as Submitted" below'
                ];
                break;
        }
    }

    generateFile(): void {
        this.generating = true;
        this.errorMsg = null;
        const eligibleIds = this.eligible.map(r => r.ownerStmtId);

        this.nachaService.generateNacha(eligibleIds, this.data.taxType).subscribe({
            next: (response: any) => {
                this.generating = false;
                this.fileGenerated = true;

                // Extract batch ID from response header
                const batchIdHeader = response.headers?.get('X-Nacha-Batch-Id');
                if (batchIdHeader) this.generatedBatchId = parseInt(batchIdHeader, 10);

                // Download the file
                const blob = response.body;
                const url  = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href  = url;
                const month = this.eligible[0]?.stmtMonth?.toString().padStart(2, '0') ?? '00';
                const year  = this.eligible[0]?.stmtYear ?? '';
                link.download = `KBM_${this.data.taxType}_${year}-${month}.ach`;
                link.click();
                URL.revokeObjectURL(url);
            },
            error: (err) => {
                this.generating = false;
                this.errorMsg = err?.error?.message ?? err?.message ?? 'File generation failed.';
                console.error(err);
            }
        });
    }

    markSubmitted(): void {
        if (this.generatedBatchId == null) return;
        this.markingSubmitted = true;
        this.nachaService.markSubmitted(this.generatedBatchId).subscribe({
            next: () => {
                this.markingSubmitted = false;
                this.submitted = true;
            },
            error: (err) => {
                this.markingSubmitted = false;
                this.errorMsg = 'Failed to mark as submitted.';
                console.error(err);
            }
        });
    }

    close(): void {
        this.dialogRef.close();
    }
}
